import { dustPolicySchema } from '../dust-evidence.ts';
import { swapCutoffHoursSchema, swapPolicySchema } from '../shift-swaps.ts';
import { z } from 'zod';
import { areaPricingRulesSchema } from '../area-pricing.ts';
import { walkthroughPolicySchema } from '../walkthrough-gate.ts';
import { normalizePhoneToE164 } from '../phone.ts';
import { MEDIA_RETENTION_CHOICES } from '../magic-quote.ts';
import {
  CANCELLED_SLOT_AUDIENCES,
  CANCELLED_SLOT_NOTICE_HOURS_MAX,
  CANCELLED_SLOT_NOTICE_HOURS_MIN,
  CANCELLED_SLOT_QUIET_HOUR_MAX,
  CANCELLED_SLOT_QUIET_HOUR_MIN,
  CANCELLED_SLOT_ROUND_HOURS_MAX,
  CANCELLED_SLOT_ROUND_HOURS_MIN,
  CANCELLED_SLOT_ROUND_SIZE_MAX,
  CANCELLED_SLOT_ROUND_SIZE_MIN,
} from '../cancelled-slots.ts';
import {
  LATE_CANCEL_NOTICE_HOURS_MAX,
  LATE_CANCEL_NOTICE_HOURS_MIN,
  lateCancelFeeAmountError,
} from '../late-cancel.ts';
import {
  BILLING_INCREMENT_MINUTES,
  FLOOR_TYPES,
  MAX_LAYOUT_ROOMS,
  MAX_ROOM_QUANTITY,
  MEASUREMENT_BASES,
  ROOM_CONDITION_CODES,
  ROOM_SIZE_CODES,
  SERVICE_FREQUENCIES,
  QUOTE_FOLLOW_UP_DAYS_MAX,
  SERIES_FREQUENCIES,
  CUSTOMER_SERVICE_SEGMENTS,
  SERVICE_MODES,
  SMS_COMPANY_DAILY_LIMIT_MAX,
  SMS_CONSENT_RECORD_SOURCES,
  SMS_CONSENT_STATUSES,
  SMS_SENDER_LABEL_MAX,
} from '../constants/index.ts';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '../constants/languages.ts';
import { canonicalLocale, DATE_FORMATS, TIME_FORMATS } from '../locale-format.ts';
import {
  basisPointsSchema,
  idSchema,
  localizedTextSchema,
  measuredQuantitySchema,
  minutesSchema,
  nonNegativeCentsSchema,
  percentBasisPointsSchema,
  productionRateSchema,
  optionalEmail,
  optionalText,
  optionalUrl,
  requiredText,
} from './common.ts';
import {
  WIDGET_BUTTON_LABEL_MAX,
  WIDGET_CORNERS,
  WIDGET_DEFAULT_ACCENT,
  WIDGET_FIELD_SETS,
  WIDGET_HEADLINE_MAX,
  WIDGET_ICONS,
  WIDGET_INTRO_MAX,
  WIDGET_MODES,
  WIDGET_POSITIONS,
  WIDGET_THEMES,
} from '../widget.ts';
import { emailFormat } from './formats.ts';

/**
 * The website widget's style options.
 *
 * Defaults are safe here, unlike the update schemas below: the settings screen
 * always submits the whole object, and the column is replaced wholesale — an
 * absent *object* leaves the stored one untouched, while an absent *field*
 * inside a submitted object genuinely means "back to the default".
 *
 * Loose on purpose: a key introduced by a newer release must survive a save
 * made through an older worker mid-rolling-deploy, so unknown properties are
 * carried rather than stripped (the PATCH route additionally merges over the
 * stored JSON — see routes/companies.ts). The renderer's sanitizer ignores
 * what it does not know, so a carried key can style nothing by accident.
 */
/**
 * The AI assist toggles, shaped like the widget settings and for the same
 * reasons: loose so unknown keys survive a rolling deploy, defaults filled in
 * by the shared sanitizer either way (src/shared/ai.ts).
 */
export const aiSettingsSchema = z.looseObject({
  enabled: z.boolean().optional(),
  leadDrafts: z.boolean().optional(),
  photoAssessments: z.boolean().optional(),
  smsIntents: z.boolean().optional(),
  tuningSuggestions: z.boolean().optional(),
  weeklyDigest: z.boolean().optional(),
  followUpCopy: z.boolean().optional(),
  crewBriefings: z.boolean().optional(),
  reviewRequests: z.boolean().optional(),
  quickQuotes: z.boolean().optional(),
  /**
   * Magic Quote's own settings, nested because it needs more than one bit:
   * which capture methods a company permits in customers' homes is a policy
   * choice, not a feature flag. Loose for the same rolling-deploy reason as
   * its parent — and merged the same way, so a client that sends only
   * `{ video: false }` does not silently switch the feature off.
   */
  magicQuote: z
    .looseObject({
      enabled: z.boolean().optional(),
      video: z.boolean().optional(),
      audio: z.boolean().optional(),
      conversationCapture: z.boolean().optional(),
      pricingSuggestions: z.boolean().optional(),
    })
    .optional(),
});

/**
 * Frequency pricing tier defaults, shaped like the AI toggles and for the
 * same reasons: loose so unknown keys survive a rolling deploy, defaults
 * filled in by the shared sanitizer either way
 * (src/shared/frequency-tiers.ts).
 */
export const frequencyTierSettingsSchema = z.looseObject({
  offerOnNewQuotes: z.boolean().optional(),
  tiers: z
    .looseObject(
      Object.fromEntries(
        SERIES_FREQUENCIES.map((frequency) => [
          frequency,
          z
            .looseObject({
              offered: z.boolean().optional(),
              discountBasisPoints: percentBasisPointsSchema.optional(),
            })
            .optional(),
        ]),
      ),
    )
    .optional(),
});

export const widgetSettingsSchema = z.looseObject({
  mode: z.enum(WIDGET_MODES).default('float'),
  position: z.enum(WIDGET_POSITIONS).default('bottom-right'),
  theme: z.enum(WIDGET_THEMES).default('light'),
  corners: z.enum(WIDGET_CORNERS).default('rounded'),
  icon: z.enum(WIDGET_ICONS).default('sparkle'),
  fieldSet: z.enum(WIDGET_FIELD_SETS).default('full'),
  accentColor: z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a six-digit hex color, like #0f5c4a')
    .transform((value) => value.toLowerCase())
    .default(WIDGET_DEFAULT_ACCENT),
  buttonLabel: optionalText(WIDGET_BUTTON_LABEL_MAX),
  headline: optionalText(WIDGET_HEADLINE_MAX),
  intro: optionalText(WIDGET_INTRO_MAX),
  showLogo: z.boolean().default(true),
});

export const updateCompanySchema = z
  .object({
    name: requiredText(200, 'Enter your company name').optional(),
    legalName: optionalText(200),
    email: optionalEmail,
    phone: optionalText(40),
    website: optionalUrl,
    addressLine1: optionalText(160),
    addressLine2: optionalText(160),
    city: optionalText(80),
    state: optionalText(80),
    postalCode: optionalText(20),
    countryCode: z
      .string()
      .trim()
      .regex(/^[A-Za-z]{2}$/, 'Use a two-letter country code')
      .transform((value) => value.toUpperCase())
      .optional(),
    // Shape only. Whether the code is one the app can actually charge in is
    // checked in `PATCH /company`, because the answer depends on what is
    // already stored: a code saved before that list existed has to stay
    // saveable, or its company can never edit any other setting again.
    currencyCode: z
      .string()
      .trim()
      .regex(/^[A-Za-z]{3}$/, 'Use a three-letter currency code')
      .transform((value) => value.toUpperCase())
      .optional(),
    /**
     * BCP 47 locale for money and dates. Null clears an explicit choice back
     * to "never chosen" (existing companies, and a restored backup of one).
     * Absent leaves the stored tag alone.
     */
    locale: z
      .string()
      .trim()
      .max(35)
      .refine((value) => canonicalLocale(value) !== null, 'Enter a locale like en-US or en-ZA')
      .transform((value) => canonicalLocale(value) as string)
      .nullable()
      .optional(),
    /** Null follows the locale. Absent leaves the stored override alone. */
    dateFormat: z.enum(DATE_FORMATS).nullable().optional(),
    timeFormat: z.enum(TIME_FORMATS).nullable().optional(),
    timezone: z.string().trim().min(1).max(64).optional(),
    /**
     * The language outbound email and texts are written in. Constrained to
     * what the app has translations for, unlike the tag accepted at signup:
     * this one is a deliberate choice from a picker, so an unusable value is a
     * mistake worth reporting rather than something to quietly fall back from.
     */
    language: z.enum(SUPPORTED_LANGUAGES).optional(),
    /**
     * What kind of cleaning this company does. Switching commercial work ON
     * (to 'commercial' or 'both') also seeds the commercial starter library,
     * once — the route handles that, since a schema cannot see what is
     * already stored.
     */
    serviceMode: z.enum(SERVICE_MODES).optional(),
    /**
     * Minutes after a visit's start with nobody clocked in before the owner
     * is alerted. Null switches the alerts off. The floor stops a company
     * alerting itself about a crew thirty seconds behind; the ceiling is the
     * migration's own.
     */
    missedClockInAlertMinutes: z.number().int().min(5).max(240).nullable().optional(),
    requireClockInToComplete: z.boolean().optional(),
    blockCompletionOnMissingPhotos: z.boolean().optional(),
    /**
     * Shift swaps (0095): owner approval always (the default), automatic when
     * both people share an active saved team at the visit's location, or off.
     */
    swapPolicy: swapPolicySchema.optional(),
    /** No swap may be asked, accepted or approved this many hours before the visit. */
    swapCutoffHours: swapCutoffHoursSchema.optional(),
    /**
     * Hourly crew cost used when Team pay is not set up, and when someone's
     * pay is missing on a visit. Wages and payroll only: it does not add
     * insurance or the van. Null clears it. Profit by place then costs from
     * Team pay alone, and shows hours with no margin where pay is missing.
     */
    laborCostCentsPerHour: nonNegativeCentsSchema
      .refine((value) => value > 0, 'A labor cost has to be greater than zero')
      .nullable()
      .optional(),
    /**
     * Hours the company promises to acknowledge a complaint in. A week is the
     * ceiling — past that it is not a target, it is an admission.
     */
    responseTargetHours: z.number().int().min(1).max(168).nullable().optional(),
    dustPolicy: dustPolicySchema.nullable().optional(),
    walkthroughPolicy: walkthroughPolicySchema.nullable().optional(),
    walkthroughPolicyRevision: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).optional(),
    areaPricingRules: areaPricingRulesSchema.nullable().optional(),
    areaPricingCurrencyCode: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .optional(),
    defaultHourlyRateCents: nonNegativeCentsSchema
      .refine((value) => value > 0, 'Hourly rate must be greater than zero')
      .optional(),
    defaultQuoteExpirationDays: z.number().int().min(1).max(365).optional(),
    taxEnabled: z.boolean().optional(),
    defaultTaxRateBasisPoints: basisPointsSchema.optional(),
    laborIsTaxable: z.boolean().optional(),
    billingIncrementMinutes: z
      .number()
      .int()
      .refine((value) => BILLING_INCREMENT_MINUTES.includes(value), 'Not a supported billing increment')
      .optional(),
    quoteNumberPrefix: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9-]{1,10}$/, 'Use up to 10 letters, numbers or hyphens')
      .optional(),
    invoiceNumberPrefix: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9-]{1,10}$/, 'Use up to 10 letters, numbers or hyphens')
      .optional(),
    defaultTerms: optionalText(20_000),
    showTimeOnQuote: z.boolean().optional(),
    // Turning this OFF needs a plan that carries `unbranded_quotes`; the
    // route checks, because a schema cannot know who is asking.
    quoteBrandingEnabled: z.boolean().optional(),
    showHourlyRateOnQuote: z.boolean().optional(),
    /**
     * Whether accepting a quote requires an explicit signature — the tick-box
     * consent, and the drawn mark where the browser can produce one. Off by
     * default; see migration 0077.
     */
    requireQuoteSignature: z.boolean().optional(),
    quoteInstantBookingEnabled: z.boolean().optional(),
    // Reminder lead times: 1 hour to 2 weeks ahead of the visit.
    appointmentConfirmLeadHours: z.number().int().min(1).max(336).optional(),
    // Working hours (0059): local wall-clock in the company's own timezone,
    // stored as HH:MM plus a comma list of weekday numbers (0 = Sunday).
    workDayStartLocal: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use the format HH:MM (24-hour)')
      .optional(),
    workDayEndLocal: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use the format HH:MM (24-hour)')
      .optional(),
    // Accepts the array the settings form sends AND the comma string the
    // company export contains — an exported company must import back whole.
    workDays: z
      .union([
        z.array(z.number().int().min(0).max(6)),
        z
          .string()
          .regex(/^[0-6](,[0-6])*$/, 'Use weekday numbers 0-6, comma separated')
          .transform((raw) => raw.split(',').map(Number)),
      ])
      .pipe(
        z
          .array(z.number().int().min(0).max(6))
          .min(1, 'Keep at least one working day')
          .max(7)
          .transform((days) => [...new Set(days)].sort((a, b) => a - b).join(',')),
      )
      .optional(),
    appointmentReminderLeadHours: z.number().int().min(1).max(336).optional(),
    smsRemindersEnabled: z.boolean().optional(),
    // An owner may lower their own ceiling but not raise it past what the
    // shared Twilio account can absorb from a single tenant.
    smsDailyLimit: z.number().int().min(1).max(SMS_COMPANY_DAILY_LIMIT_MAX).optional(),
    smsSenderLabel: optionalText(SMS_SENDER_LABEL_MAX),
    // Quote follow-ups: 0 switches each off. Bounded well under any quote's
    // realistic shelf life; a nudge a month later is not a nudge.
    quoteFollowUpDays: z.number().int().min(0).max(QUOTE_FOLLOW_UP_DAYS_MAX).optional(),
    quoteExpiryWarningDays: z.number().int().min(0).max(QUOTE_FOLLOW_UP_DAYS_MAX).optional(),
    notifyOnQuoteEvents: z.boolean().optional(),
    widgetSettings: widgetSettingsSchema.optional(),
    // The AI assist toggles. Loose for the same rolling-deploy reason as the
    // widget's, and merged over the stored JSON in the PATCH route; the
    // sanitizer in shared/ai.ts supplies defaults for anything missing.
    aiSettings: aiSettingsSchema.optional(),
    // Frequency pricing tier defaults — same JSON-column treatment as the two
    // above; the sanitizer in shared/frequency-tiers.ts supplies the rest.
    frequencyTierSettings: frequencyTierSettingsSchema.optional(),
    // Where post-visit review requests point. Null switches them off even
    // with the toggle on — a request with nowhere to send people is nothing.
    reviewRequestUrl: optionalUrl,
    // How long Magic Quote keeps walkthrough media, in days. Null is "keep
    // it", which is what every company had before the setting existed; the
    // sanitizer in shared/magic-quote.ts pins anything else to a real choice.
    magicQuoteRetentionDays: z
      .number()
      .int()
      .refine((value) => (MEDIA_RETENTION_CHOICES as readonly number[]).includes(value), 'Not a supported window')
      .nullable()
      .optional(),
    // Who hears about a reschedule request, and whether it is also texted.
    notifyOwnersOnChangeRequest: z.boolean().optional(),
    notifyAssigneeOnChangeRequest: z.boolean().optional(),
    textChangeRequestAlerts: z.boolean().optional(),
    // Owners and managers hear when a visit is canceled. On unless switched off.
    notifyOwnersOnVisitCancelled: z.boolean().optional(),
    // Whether each invoice reaches the customer once its cleaning has started,
    // and whether that notice is also texted.
    emailInvoicesToCustomers: z.boolean().optional(),
    textInvoicesToCustomers: z.boolean().optional(),
    // Whether a receipt is emailed automatically when a payment is recorded or
    // settles. On unless switched off. Every plan, Free included.
    emailReceiptsToCustomers: z.boolean().optional(),
    // Whether customers are offered a tip when paying — online, in person, or
    // recorded with a manual payment. Off unless switched on; locations may
    // override it either way.
    tipsEnabled: z.boolean().optional(),
    // Late cancellation fee. Off until switched on; the hours and the amount
    // are stored either way so turning it on starts from a real policy.
    lateCancelFeeEnabled: z.boolean().optional(),
    lateCancelNoticeHours: z
      .number()
      .int()
      .min(LATE_CANCEL_NOTICE_HOURS_MIN)
      .max(LATE_CANCEL_NOTICE_HOURS_MAX)
      .optional(),
    lateCancelFeeCents: nonNegativeCentsSchema.optional(),
    // Filling a canceled spot. Off until switched on. The numbers are stored
    // either way so turning it on starts from a real policy. The offer stays
    // open until the notice cutoff. People hear in groups.
    autoOfferCancelledSlots: z.boolean().optional(),
    cancelledSlotNoticeHours: z
      .number()
      .int()
      .min(CANCELLED_SLOT_NOTICE_HOURS_MIN)
      .max(CANCELLED_SLOT_NOTICE_HOURS_MAX)
      .optional(),
    cancelledSlotRoundHours: z
      .number()
      .int()
      .min(CANCELLED_SLOT_ROUND_HOURS_MIN)
      .max(CANCELLED_SLOT_ROUND_HOURS_MAX)
      .optional(),
    cancelledSlotRoundSize: z
      .number()
      .int()
      .min(CANCELLED_SLOT_ROUND_SIZE_MIN)
      .max(CANCELLED_SLOT_ROUND_SIZE_MAX)
      .optional(),
    cancelledSlotQuietStartHour: z
      .number()
      .int()
      .min(CANCELLED_SLOT_QUIET_HOUR_MIN)
      .max(CANCELLED_SLOT_QUIET_HOUR_MAX)
      .optional(),
    cancelledSlotQuietEndHour: z
      .number()
      .int()
      .min(CANCELLED_SLOT_QUIET_HOUR_MIN)
      .max(CANCELLED_SLOT_QUIET_HOUR_MAX)
      .optional(),
    cancelledSlotTeamScope: z.enum(['same_team', 'any_eligible']).optional(),
    cancelledSlotRequireApproval: z.boolean().optional(),
    cancelledSlotOfferAudience: z.enum(CANCELLED_SLOT_AUDIENCES).optional(),
  })
  .superRefine((value, ctx) => {
    // The flow is request-then-remind; when both are in one payload the order
    // can be checked here. A single-field PATCH is checked against the stored
    // company in the route, which knows the other half.
    if (
      value.appointmentConfirmLeadHours !== undefined &&
      value.appointmentReminderLeadHours !== undefined &&
      value.appointmentConfirmLeadHours <= value.appointmentReminderLeadHours
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['appointmentConfirmLeadHours'],
        message: 'The confirmation request must go out earlier than the reminder',
      });
    }
    // An end BEFORE the start is a night shift — 22:00–06:00 is how most
    // commercial cleaning runs — and is accepted. An end EQUAL to the start is
    // a window zero minutes long, which is not a shift at any hour.
    if (
      value.workDayStartLocal !== undefined &&
      value.workDayEndLocal !== undefined &&
      value.workDayEndLocal === value.workDayStartLocal
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['workDayEndLocal'],
        message: 'The working day cannot end at the same time it starts',
      });
    }
    if (
      value.lateCancelFeeCents !== undefined &&
      value.currencyCode &&
      (value.lateCancelFeeEnabled === true || value.lateCancelFeeCents > 0)
    ) {
      const message = lateCancelFeeAmountError(value.lateCancelFeeCents, value.currencyCode);
      if (message) {
        ctx.addIssue({ code: 'custom', path: ['lateCancelFeeCents'], message });
      }
    }
  });

/** A Twilio Messaging Service SID: MG and 32 hex characters. Also checked by the showcase seeder. */
export const MESSAGING_SERVICE_SID_PATTERN = /^MG[0-9a-fA-F]{32}$/;

/**
 * A company's own Twilio account.
 *
 * Shapes only — every value here is a fixed format Twilio itself publishes, and
 * checking them in the form is the difference between "that is not an Account
 * SID" while the owner is looking at the field and a mystified support ticket
 * about reminders that silently never arrive. Whether the credentials actually
 * work is Twilio's answer to give, and the first message is what asks.
 *
 * Every field is optional so the page can rotate one of them without resending
 * the rest; what a first connection must include is checked in
 * `saveCompanySmsCredentials`, which knows what is already stored.
 */
export const companySmsSettingsSchema = z.object({
  accountSid: z
    .string()
    .trim()
    .regex(/^AC[0-9a-fA-F]{32}$/, 'That does not look like a Twilio Account SID (AC followed by 32 characters)')
    .optional(),
  /** Twilio's auth tokens are 32 hex characters. Never returned once stored. */
  authToken: z
    .string()
    .trim()
    .regex(/^[0-9a-fA-F]{32}$/, 'That does not look like a Twilio Auth Token (32 characters)')
    .optional(),
  /**
   * An API key SID for REST calls. An empty string removes the stored key.
   * The secret is never returned once stored.
   */
  apiKeySid: z
    .string()
    .trim()
    .refine(
      (value) => value === '' || /^SK[0-9a-fA-F]{32}$/.test(value),
      'That does not look like a Twilio API key SID (SK followed by 32 characters)',
    )
    .optional(),
  apiKeySecret: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9]{16,64}$/, 'That does not look like a Twilio API key secret')
    .optional(),
  /** An empty string clears the stored Messaging Service. */
  messagingServiceSid: z
    .string()
    .trim()
    .refine(
      (value) => value === '' || MESSAGING_SERVICE_SID_PATTERN.test(value),
      'That does not look like a Twilio Messaging Service SID (MG followed by 32 characters)',
    )
    .optional(),
  /**
   * The fallback sender, in E.164. Twilio will not accept anything else, and a
   * number typed the way a customer would say it is the likeliest mistake here.
   * An empty string clears it.
   */
  fromNumber: z
    .string()
    .trim()
    .refine(
      (value) => value === '' || /^\+[1-9]\d{7,14}$/.test(value),
      'Enter the number in international format, starting with + (for example +15551234567)',
    )
    .optional(),
  /** Switches the company's own account off without discarding it. */
  enabled: z.boolean().optional(),
});

export type CompanySmsSettingsInput = z.infer<typeof companySmsSettingsSchema>;

/**
 * "Set up Twilio again" on the own-account card. With no body it sets up what
 * can be set up without asking; `confirm` is the owner agreeing to one of the
 * takeovers the card offered, naming the `target` it was shown so a different
 * one (the console changed in between) is never taken over on that answer.
 */
export const twilioSetupSchema = z.object({
  confirm: z
    .object({
      option: z.enum(['take_over', 'use_service']),
      target: z.string().trim().min(1).max(128),
    })
    .optional(),
});

export type TwilioSetupInput = z.infer<typeof twilioSetupSchema>;

/**
 * Starting Twilio Connect. The website sends nothing; the mobile app sends
 * `client: 'native'`, so the approval comes back to the app
 * (TWILIO_CONNECT_NATIVE_CALLBACK_URL) instead of to Settings in a browser
 * the app cannot read.
 */
export const twilioConnectStartSchema = z.object({
  client: z.enum(['web', 'native']).optional(),
});

/**
 * Finishing a Twilio Connect the mobile app started: the state and the
 * Account SID the callback handed back to the app, as Twilio gave them.
 */
export const twilioConnectFinishSchema = z.object({
  state: z.string().trim().min(1).max(4096),
  accountSid: z.string().trim().min(1).max(64),
});

/**
 * The number BlastoClean buys in a Twilio account connected through Twilio
 * Connect: a US area code, and nothing else. Which account, and where its
 * texts and calls go, is the server's to decide.
 */
export const twilioConnectNumberSchema = z.object({
  areaCode: z
    .string()
    .trim()
    .regex(/^[2-9]\d{2}$/, 'Enter a three-digit US area code.'),
});

export type TwilioConnectNumberInput = z.infer<typeof twilioConnectNumberSchema>;

/** An https address Twilio's reviewers can open, for the privacy and terms links. */
const httpsUrl = z
  .string()
  .trim()
  .max(500)
  .refine((value) => {
    try {
      return new URL(value).protocol === 'https:';
    } catch {
      return false;
    }
  }, 'Enter an https:// address.');

/**
 * The texting registration (A2P 10DLC campaign) BlastoClean files in the
 * company's own Twilio account, as the owner edited it. Twilio's own limits:
 * a description of 40 to 4,096 characters, a message flow of 40 to 2,048, two
 * to five samples of 20 to 1,024 each, an opt-in text of 20 to 320, and the
 * privacy and terms pages as https links (services/own-a2p.ts).
 */
export const ownA2pCampaignContentSchema = z.object({
  description: z.string().trim().min(40, 'Write at least 40 characters.').max(4096),
  messageFlow: z.string().trim().min(40, 'Write at least 40 characters.').max(2048),
  samples: z
    .array(z.string().trim().min(20, 'Each sample needs at least 20 characters.').max(1024))
    .min(2, 'Give at least two sample texts.')
    .max(5, 'Give at most five sample texts.'),
  optInMessage: z.string().trim().min(20, 'Write at least 20 characters.').max(320),
  privacyPolicyUrl: httpsUrl,
  termsAndConditionsUrl: httpsUrl,
});

export type OwnA2pCampaignContent = z.infer<typeof ownA2pCampaignContentSchema>;

/** Filing (or sending back) the texting registration on one of the account's approved brands. */
export const ownA2pSubmitSchema = z.object({
  brandSid: z
    .string()
    .trim()
    .regex(/^BN[0-9a-fA-F]{32}$/, 'Choose your approved business registration.'),
  content: ownA2pCampaignContentSchema,
  /**
   * File a rejected registration again as a new one: the rejected one is
   * deleted and a new one filed, which Twilio charges the review fee for
   * again. Only when Twilio would not take the edit in place, which it
   * offers to some accounts only, and only after the owner agreed to it.
   * The SID of the rejected campaign they agreed to replace: one replaced
   * meanwhile (another tab, another owner, the Console) is not deleted.
   */
  refile: z
    .string()
    .trim()
    .regex(/^QE[0-9a-fA-F]{32}$/)
    .optional(),
});

/**
 * Rejection codes the carriers do not review again (Twilio: appeal through
 * its support instead). Neither an edit nor a new filing is offered.
 */
export const OWN_A2P_FINAL_REJECTIONS: readonly number[] = [30883, 30884, 30885, 30897];

/** The keywords a customer texts to opt in, as a filing declares them. */
export const OWN_A2P_OPT_IN_KEYWORDS: readonly string[] = ['START'];

const PHONE_NUMBER = /(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/;

/**
 * Whether the texts filed carry a phone number, as Twilio asks to be told
 * (`HasEmbeddedPhone`): a number in a sample or the opt-in confirmation.
 * Leaving it out of a filing whose samples show one gets it rejected. The
 * same answer for BlastoClean's filing and the do-it-yourself kit's.
 */
export function ownA2pMentionsPhoneNumber(content: { samples: string[]; optInMessage: string }): boolean {
  return [...content.samples, content.optInMessage].some((text) => PHONE_NUMBER.test(text));
}

export type OwnA2pSubmitInput = z.infer<typeof ownA2pSubmitSchema>;

/**
 * Asking the operator for a dedicated sending number.
 *
 * One optional field, which is the point: everything else the request needs —
 * which Twilio account, which webhook, which Messaging Service — belongs to the
 * operator and is never the tenant's to supply.
 *
 * The area code is a preference rather than a requirement. Omitted, the
 * company's own phone number supplies one; unavailable, provisioning widens the
 * search nationwide rather than failing, because a working number somewhere
 * beats no number at all.
 */
const US_REGION_CODES = new Set([
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'AS',
  'GU',
  'MP',
  'PR',
  'VI',
]);

const BUSINESS_TYPES = ['llc', 'corporation', 'partnership', 'cooperative', 'nonprofit'] as const;

const managedNumberRegistrationSchema = z
  .object({
    kind: z.enum(['standard', 'sole_proprietor']),
    legalName: requiredText(120, 'Enter the legal business name'),
    ein: z.string().trim().optional(),
    businessType: z.enum(BUSINESS_TYPES).optional(),
    addressLine1: requiredText(120, 'Enter the street address'),
    addressLine2: optionalText(120),
    city: requiredText(80, 'Enter the city'),
    region: z.string().trim().toUpperCase(),
    postalCode: z.string().trim(),
    website: z.string().trim().max(200).optional(),
    contactFirstName: requiredText(60, 'Enter a first name'),
    contactLastName: requiredText(60, 'Enter a last name'),
    contactEmail: z.string().trim().email(emailFormat('Enter an email address')).max(200),
    contactPhone: requiredText(30, 'Enter a phone number'),
    mobilePhone: z.string().trim().optional(),
  })
  .superRefine((value, ctx) => {
    if (!US_REGION_CODES.has(value.region)) {
      ctx.addIssue({ code: 'custom', path: ['region'], message: 'Enter a two-letter US state' });
    }
    if (!/^\d{5}(-\d{4})?$/.test(value.postalCode)) {
      ctx.addIssue({ code: 'custom', path: ['postalCode'], message: 'Enter a ZIP code' });
    }
    const website = value.website?.trim() ?? '';
    if (website) {
      const withScheme = /^https?:\/\//i.test(website) ? website : `https://${website}`;
      try {
        const url = new URL(withScheme);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') throw new Error('bad');
        if (!url.hostname.includes('.')) {
          ctx.addIssue({ code: 'custom', path: ['website'], message: 'Enter a website like example.com' });
        }
      } catch {
        ctx.addIssue({ code: 'custom', path: ['website'], message: 'Enter a website like example.com' });
      }
    }
    if (!normalizePhoneToE164(value.contactPhone, 'US')) {
      ctx.addIssue({ code: 'custom', path: ['contactPhone'], message: 'Enter a phone number we can text' });
    }
    const einDigits = (value.ein ?? '').replace(/\D/g, '');
    const hasEin =
      /^\d{9}$/.test(einDigits) &&
      einDigits !== '000000000' &&
      einDigits !== '123456789' &&
      !/^(\d)\1{8}$/.test(einDigits);

    if (value.kind === 'standard') {
      if (!hasEin) {
        ctx.addIssue({
          code: 'custom',
          path: ['ein'],
          message: /^\d{9}$/.test(einDigits)
            ? "That tax ID isn't valid. Enter the 9 digits from your IRS paperwork."
            : 'Enter the 9-digit tax ID',
        });
      }
      if (!value.businessType) {
        ctx.addIssue({ code: 'custom', path: ['businessType'], message: 'Choose a business type' });
      }
      if (!website) {
        ctx.addIssue({
          code: 'custom',
          path: ['website'],
          message: 'Enter your business website so the phone carriers can verify it',
        });
      }
    }
    if (value.kind === 'sole_proprietor') {
      if (einDigits.length > 0 && !hasEin) {
        ctx.addIssue({
          code: 'custom',
          path: ['ein'],
          message: /^\d{9}$/.test(einDigits)
            ? "That tax ID isn't valid. Enter the 9 digits from your IRS paperwork."
            : 'Enter the 9-digit tax ID, or leave it blank if you do not have one',
        });
      }
      if (hasEin) {
        if (!website) {
          ctx.addIssue({
            code: 'custom',
            path: ['website'],
            message: 'Enter your business website so the phone carriers can verify it',
          });
        }
      } else {
        const mobile = normalizePhoneToE164(value.mobilePhone ?? '', 'US');
        if (!mobile?.startsWith('+1')) {
          ctx.addIssue({ code: 'custom', path: ['mobilePhone'], message: 'Enter a US mobile number' });
        }
      }
    }
  });

/**
 * Asking for a dedicated texting number.
 *
 * `registration` is the short business form. Omitted, the request still queues
 * a number and waits for a registration filed by hand — older clients do that.
 */
export const managedNumberRequestSchema = z.object({
  areaCode: z
    .string()
    .trim()
    .refine((value) => value === '' || /^[0-9]{3}$/.test(value), 'An area code is three digits')
    .optional(),
  registration: managedNumberRegistrationSchema.optional(),
});

export type ManagedNumberRequestInput = z.infer<typeof managedNumberRequestSchema>;

/**
 * "Does this actually work?" — one message, to a number the sender nominates.
 *
 * The number is required rather than defaulted server-side. The form fills it
 * in from the sender's own profile, but the *server* defaulting it would mean a
 * misconfigured account could text somebody by omission, and there is no
 * consent record standing behind this message to catch that.
 */
export const sendTestSmsSchema = z.object({
  recipientPhone: requiredText(40, 'Enter a mobile number to text'),
});

/**
 * The stored fields, with no defaults on any of them.
 *
 * Defaults are added for create below and deliberately kept off the update
 * schemas: `.partial()` makes a field optional but keeps its default, so a
 * defaulted field is never absent from a parsed PATCH — it arrives carrying a
 * value the caller never sent, which the update queries then write over the
 * stored one. `{ defaultMinutes: 20 }` would silently re-activate a hidden
 * task, make it taxable, put it back on customer quotes and reset its position.
 */
/** A cost amount: whole cents, bounded by the 0118 CHECKs. */
const costCentsSchema = z
  .number()
  .int('Amounts are whole cents')
  .min(0, 'Cannot be negative')
  .max(100_000_000, 'That amount is too large');

const serviceTemplateFields = z.object({
  name: requiredText(160, 'Give the task a name'),
  customerDescription: optionalText(1000),
  /**
   * The two texts above in the company's other languages (see localized-text.ts).
   *
   * Not required, and deliberately not defaulted: an owner who only fills in
   * one language has a library that reads correctly in one language, which is
   * where every company was before this existed. The starter library arrives
   * with both filled in, so the common case is that these are already right
   * and nobody has to think about them.
   */
  nameTranslations: localizedTextSchema(160),
  customerDescriptionTranslations: localizedTextSchema(1000),
  categoryTranslations: localizedTextSchema(80),
  /**
   * Which language the base texts above are typed in.
   *
   * Sent by the client because the client is where the typing happened — the
   * Worker only knows what the company SENDS in, and that is a different
   * question from what language this row was written in. Absent means English,
   * which is what every row meant before the field existed.
   */
  textLanguage: z.enum(SUPPORTED_LANGUAGES),
  category: optionalText(80),
  categoryCode: z.string().trim().max(40).nullable().optional(),
  tags: z
    .object({
      segments: z.array(z.enum(['residential', 'commercial'])).max(2),
      jobTypes: z.array(z.enum(['standard', 'deep', 'move_out', 'post_construction'])).max(4),
      floorTypes: z.array(z.enum(FLOOR_TYPES)).max(8),
    })
    .nullable()
    .optional(),
  isFavorite: z.boolean().optional(),
  defaultMinutes: minutesSchema,
  minimumMinutes: minutesSchema.nullable().optional(),
  maximumMinutes: minutesSchema.nullable().optional(),
  isTaxable: z.boolean(),
  isActive: z.boolean(),
  showOnCustomerQuote: z.boolean(),
  /** How this task is usually estimated, and the rate to start from (0086). */
  measurementBasis: z.enum(MEASUREMENT_BASES),
  productionRatePerHour: productionRateSchema.nullable().optional(),
  /**
   * The task's cost basis (0118), for the margin each quote line is expected
   * to make: its labor's loaded hourly rate (null = the company default in
   * Settings → Margin floor), and the materials and travel one room of it
   * costs per visit. Staff only; never on a customer document.
   */
  costLoadedHourlyCents: costCentsSchema.nullable().optional(),
  costMaterialsCents: costCentsSchema.optional(),
  costTravelCents: costCentsSchema.optional(),
  displayOrder: z.number().int().min(0).max(10_000),
});

const createServiceTemplateFields = serviceTemplateFields.extend({
  textLanguage: z.enum(SUPPORTED_LANGUAGES).default(DEFAULT_LANGUAGE),
  isTaxable: z.boolean().default(true),
  isActive: z.boolean().default(true),
  showOnCustomerQuote: z.boolean().default(true),
  measurementBasis: z.enum(MEASUREMENT_BASES).default('time'),
  displayOrder: z.number().int().min(0).max(10_000).default(0),
});

/**
 * Applied to both the create and update schemas.
 *
 * The refinement lives on a shared helper rather than on the base object
 * because Zod cannot derive a `.partial()` from a refined schema — the two
 * schemas are built from the same fields and then each refined.
 */
const minutesRangeIsSane = (value: {
  minimumMinutes?: number | null | undefined;
  maximumMinutes?: number | null | undefined;
}) => value.minimumMinutes == null || value.maximumMinutes == null || value.minimumMinutes <= value.maximumMinutes;

const MINUTES_RANGE_ERROR = { message: 'Minimum cannot exceed maximum', path: ['minimumMinutes'] };

export const createServiceTemplateSchema = createServiceTemplateFields.refine(minutesRangeIsSane, MINUTES_RANGE_ERROR);

/**
 * No range refinement here: a PATCH may carry only one bound, and comparing it
 * against an absent one would let `{ maximumMinutes: 30 }` through onto a
 * template whose stored minimum is 60. The invariant belongs to the merged
 * template and is checked with `minutesRangeIsSane` after the merge.
 */
export const updateServiceTemplateSchema = serviceTemplateFields.partial();

export { minutesRangeIsSane };

export const roomTemplateServiceLinkSchema = z.object({
  serviceTemplateId: z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/),
  defaultSelected: z.boolean().default(false),
  defaultMinutesOverride: minutesSchema.nullable().optional(),
  /** The rate for this task IN THIS AREA, when it differs from the task's. */
  defaultProductionRateOverride: productionRateSchema.nullable().optional(),
  /** Fixtures or square feet this task starts with inside this room type. */
  defaultMeasuredQuantity: measuredQuantitySchema.nullable().optional(),
  displayOrder: z.number().int().min(0).max(10_000).default(0),
});

/** Defaults are kept off these for the reason on `serviceTemplateFields`. */
const roomTemplateFields = z.object({
  name: requiredText(120, 'Give the room type a name'),
  description: optionalText(500),
  /** The two texts above in the company's other languages (see localized-text.ts). */
  nameTranslations: localizedTextSchema(120),
  descriptionTranslations: localizedTextSchema(500),
  /** Which language they are typed in; see `serviceTemplateFields`. */
  textLanguage: z.enum(SUPPORTED_LANGUAGES),
  iconName: optionalText(60),
  /**
   * A standard price for this room type, charged instead of its time
   * (spec §9.12). `null` — the default, and every room type before flat
   * pricing — prices the room from its task minutes.
   */
  defaultFlatPriceCents: nonNegativeCentsSchema.nullable().optional(),
  /** Whether this kind of area is measured, and what is usually underfoot. */
  measurementBasis: z.enum(MEASUREMENT_BASES),
  defaultFloorType: z.enum(FLOOR_TYPES).nullable().optional(),
  categoryCode: z.string().trim().max(40).nullable().optional(),
  tags: z
    .object({
      segments: z.array(z.enum(['residential', 'commercial'])).max(2),
      jobTypes: z.array(z.enum(['standard', 'deep', 'move_out', 'post_construction'])).max(4),
      floorTypes: z.array(z.enum(FLOOR_TYPES)).max(8),
    })
    .nullable()
    .optional(),
  isFavorite: z.boolean().optional(),
  defaultQuantity: z.number().int().min(1).max(MAX_ROOM_QUANTITY).optional(),
  defaultSquareFeet: measuredQuantitySchema.nullable().optional(),
  displayOrder: z.number().int().min(0).max(10_000),
  isActive: z.boolean(),
  services: z.array(roomTemplateServiceLinkSchema).max(200),
});

export const createRoomTemplateSchema = roomTemplateFields.extend({
  textLanguage: z.enum(SUPPORTED_LANGUAGES).default(DEFAULT_LANGUAGE),
  measurementBasis: z.enum(MEASUREMENT_BASES).default('time'),
  displayOrder: z.number().int().min(0).max(10_000).default(0),
  isActive: z.boolean().default(true),
  services: z.array(roomTemplateServiceLinkSchema).max(200).default([]),
});

/**
 * Reordering the whole library in one request.
 *
 * The complete ordering, not a pair to swap: room types can share a display
 * order (the seed numbers them, later edits need not), and hidden ones sit in
 * the same sequence while being absent from the screen the arrows are on. The
 * server renumbers from this list in a single batch, so a move either lands
 * whole or not at all.
 */
export const reorderRoomTemplatesSchema = z.object({
  ids: z.array(idSchema).min(1).max(500),
});

/**
 * `services` matters most here: a default would make it an empty array on every
 * partial PATCH, and the route replaces the task list whenever one is supplied
 * — so renaming a room type, or restoring a hidden one, would take every task
 * off it. Absent now means absent, and the stored list is left alone.
 */
export const updateRoomTemplateSchema = roomTemplateFields.partial();

const groupTemplateServiceSchema = z.object({
  serviceTemplateId: idSchema.nullable(),
  /** Starter-library key. Kept so a snapshot can be linked after the task exists. */
  serviceKey: z.string().trim().max(64).nullable().optional(),
  name: requiredText(160, 'Give the task a name'),
  description: optionalText(1000),
  nameTranslations: localizedTextSchema(160),
  descriptionTranslations: localizedTextSchema(1000),
  estimatedMinutes: minutesSchema,
  isSelected: z.boolean(),
  showOnCustomerQuote: z.boolean(),
  isTaxable: z.boolean(),
  frequency: z.enum(SERVICE_FREQUENCIES),
  measurementBasis: z.enum(MEASUREMENT_BASES),
  measuredQuantity: measuredQuantitySchema.nullable().optional(),
  productionRatePerHour: productionRateSchema.nullable().optional(),
});

const groupTemplateRoomSchema = z.object({
  roomTemplateId: idSchema.nullable().optional(),
  // Null is how a room that used to be a starter type is detached: omitting
  // the key would also clear it, but the editor sends null, and a string-only
  // field rejects the save.
  roomKey: z.string().trim().min(1).max(64).nullable().optional(),
  roomName: requiredText(120, 'Give the room a name'),
  roomNameTranslations: localizedTextSchema(120),
  quantity: z.number().int().min(1).max(MAX_ROOM_QUANTITY),
  sizeCode: z.enum(ROOM_SIZE_CODES as [string, ...string[]]),
  conditionCode: z.enum(ROOM_CONDITION_CODES as [string, ...string[]]),
  flatPriceCents: nonNegativeCentsSchema.nullable().optional(),
  measuredSquareFeet: measuredQuantitySchema.nullable().optional(),
  floorType: z.enum(FLOOR_TYPES).nullable().optional(),
  customerNotes: optionalText(2000),
  /**
   * The estimator's typed duration, when they overrode the task sum. Absent
   * means the saved layout is still priced from its tasks.
   */
  adjustedMinutes: minutesSchema.nullable().optional(),
  services: z.array(groupTemplateServiceSchema).max(200),
});

const templateTagsSchema = z.object({
  segments: z.array(z.enum(['residential', 'commercial'])).max(2),
  jobTypes: z.array(z.enum(['standard', 'deep', 'move_out', 'post_construction'])).max(4),
  floorTypes: z.array(z.enum(FLOOR_TYPES)).max(8),
});

export const createGroupTemplateSchema = z.object({
  name: requiredText(160, 'Give the group a name'),
  description: optionalText(500),
  nameTranslations: localizedTextSchema(160),
  descriptionTranslations: localizedTextSchema(500),
  textLanguage: z.enum(SUPPORTED_LANGUAGES).default(DEFAULT_LANGUAGE),
  notes: optionalText(2000),
  categoryCode: z.string().trim().max(40).nullable().optional(),
  tags: templateTagsSchema.nullable().optional(),
  isFavorite: z.boolean().optional(),
  rooms: z.array(groupTemplateRoomSchema).min(1).max(MAX_LAYOUT_ROOMS),
});

/**
 * Hide, restore, favorite, categorize, or rewrite a saved group.
 *
 * Name, notes and rooms are optional so a favorite toggle still sends only
 * `isFavorite`. When `rooms` is present the stored layout is replaced.
 * Absent fields stay as stored.
 */
export const updateGroupTemplateSchema = z
  .object({
    isActive: z.boolean().optional(),
    isFavorite: z.boolean().optional(),
    categoryCode: z.string().trim().max(40).nullable().optional(),
    tags: templateTagsSchema.nullable().optional(),
    name: z.string().trim().min(1, 'Give the group a name').max(160).optional(),
    description: optionalText(500),
    notes: optionalText(2000),
    rooms: z.array(groupTemplateRoomSchema).min(1).max(MAX_LAYOUT_ROOMS).optional(),
  })
  .refine(
    (value) =>
      value.isActive !== undefined ||
      value.isFavorite !== undefined ||
      value.categoryCode !== undefined ||
      value.tags !== undefined ||
      value.name !== undefined ||
      value.description !== undefined ||
      value.notes !== undefined ||
      value.rooms !== undefined,
    { message: 'Nothing to update' },
  );

/**
 * Shape of an export file being imported (spec §10.10).
 *
 * Deliberately permissive about unknown keys — a file produced by a newer
 * version of the app should still import what this version understands — but
 * strict about the fields it actually reads.
 */
const importedServiceTemplateSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(160),
  customer_description: z.string().max(1000).nullish(),
  /*
    The stored JSON translation maps, carried across verbatim (see localized-text.ts). Kept for
    the same reason the flat price below is: restoring a backup that silently
    dropped them would leave a bilingual library readable in one language, and
    nothing on screen would say what had gone. Validated as opaque text rather
    than re-parsed — `parseLocalizedText` is total, so a map this build cannot
    read costs the base name rather than a failed import.
  */
  name_i18n: z.string().max(4000).nullish(),
  customer_description_i18n: z.string().max(20_000).nullish(),
  category_i18n: z.string().max(2000).nullish(),
  text_language: z.string().max(16).nullish(),
  category: z.string().max(80).nullish(),
  default_minutes: z.number().int().min(0).max(100_000),
  minimum_minutes: z.number().int().min(0).max(100_000).nullish(),
  maximum_minutes: z.number().int().min(0).max(100_000).nullish(),
  is_taxable: z.number().int().min(0).max(1).nullish(),
  is_active: z.number().int().min(0).max(1).nullish(),
  show_on_customer_quote: z.number().int().min(0).max(1).nullish(),
  display_order: z.number().int().min(0).max(10_000).nullish(),
  measurement_basis: z.enum(MEASUREMENT_BASES).nullish(),
  production_rate_per_hour: productionRateSchema.nullish(),
  category_code: z.string().max(40).nullish(),
  tags_json: z.string().max(4000).nullish(),
  is_favorite: z.number().int().min(0).max(1).nullish(),
  seed_key: z.string().max(64).nullish(),
  // Cost basis (0118), so an export re-imports with the costs the margin gate reads.
  cost_loaded_hourly_cents: costCentsSchema.nullish(),
  cost_materials_cents: costCentsSchema.nullish(),
  cost_travel_cents: costCentsSchema.nullish(),
});

const importedRoomTemplateSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullish(),
  /** The stored JSON translation maps (see localized-text.ts); see the task schema above. */
  name_i18n: z.string().max(4000).nullish(),
  description_i18n: z.string().max(10_000).nullish(),
  text_language: z.string().max(16).nullish(),
  icon_name: z.string().max(60).nullish(),
  // Kept, or restoring a backup silently un-prices every room type and the
  // company's next quotes quietly go back to hourly.
  default_flat_price_cents: z.number().int().min(0).max(100_000_000).nullish(),
  display_order: z.number().int().min(0).max(10_000).nullish(),
  is_active: z.number().int().min(0).max(1).nullish(),
  measurement_basis: z.enum(MEASUREMENT_BASES).nullish(),
  default_floor_type: z.enum(FLOOR_TYPES).nullish(),
  category_code: z.string().max(40).nullish(),
  tags_json: z.string().max(4000).nullish(),
  is_favorite: z.number().int().min(0).max(1).nullish(),
  seed_key: z.string().max(64).nullish(),
  default_quantity: z.number().int().min(1).max(MAX_ROOM_QUANTITY).nullish(),
  default_square_feet: measuredQuantitySchema.nullish(),
  services: z
    .array(
      z.object({
        service_template_id: z.string().min(1),
        default_selected: z.number().int().min(0).max(1).nullish(),
        default_minutes_override: z.number().int().min(0).max(100_000).nullish(),
        default_production_rate_override: productionRateSchema.nullish(),
        default_measured_quantity: measuredQuantitySchema.nullish(),
        display_order: z.number().int().min(0).max(10_000).nullish(),
      }),
    )
    .max(500)
    .nullish(),
});

const importedCustomerSchema = z
  .object({
    id: z.string().min(1),
    first_name: z.string().max(80).nullish(),
    last_name: z.string().max(80).nullish(),
    company_name: z.string().max(160).nullish(),
    email: z.string().max(320).nullish(),
    phone: z.string().max(40).nullish(),
    billing_address_line_1: z.string().max(160).nullish(),
    billing_address_line_2: z.string().max(160).nullish(),
    billing_city: z.string().max(80).nullish(),
    billing_state: z.string().max(80).nullish(),
    billing_postal_code: z.string().max(20).nullish(),
    billing_country_code: z.string().max(2).nullish(),
    internal_notes: z.string().max(4000).nullish(),
    // Commercial billing terms (0082). Dropped, these would silently turn a
    // restored commercial book back into due-on-receipt per-visit billing —
    // a change to what every subsequent invoice says and sends.
    invoice_terms_days: z.number().int().min(0).max(365).nullish(),
    invoice_po_number: z.string().max(120).nullish(),
    consolidated_billing: z.number().int().min(0).max(1).nullish(),
    sms_marketing_enabled: z.number().int().min(0).max(1).nullish(),
    email_marketing_enabled: z.number().int().min(0).max(1).nullish(),
    // Home vs business (0091). Dropped, a restore would silently turn every
    // commercial customer back into residential via the column default.
    service_segment: z.enum(CUSTOMER_SERVICE_SEGMENTS).nullish(),
    reengagement_do_not_contact: z.number().int().min(0).max(1).nullish(),
    // Text-reminder consent, so restoring an export does not quietly discard
    // every opt-out a company has been given. What an import may *assert* is
    // constrained on the way in — see `importedSmsConsent`.
    sms_consent_status: z.enum(SMS_CONSENT_STATUSES).nullish(),
    sms_consent_phone: z.string().max(20).nullish(),
    sms_consent_at: z.string().max(40).nullish(),
    // Includes `web_form`, which the public customer response does not. A
    // company export stores the column as written; a restore has to accept it.
    sms_consent_source: z.enum(SMS_CONSENT_RECORD_SOURCES).nullish(),
  })
  // The same invariant the API enforces: an import must not be a way to create
  // nameless customers nobody can find again.
  .refine((value) => Boolean(value.first_name?.trim() || value.last_name?.trim() || value.company_name?.trim()), {
    message: 'Every imported customer needs a first name, last name or company name',
    path: ['last_name'],
  });

const importedPropertySchema = z.object({
  customer_id: z.string().min(1),
  name: z.string().max(120).nullish(),
  property_type: z.string().max(40).nullish(),
  address_line_1: z.string().max(160).nullish(),
  address_line_2: z.string().max(160).nullish(),
  city: z.string().max(80).nullish(),
  state: z.string().max(80).nullish(),
  postal_code: z.string().max(20).nullish(),
  country_code: z.string().max(2).nullish(),
  square_feet: z.number().int().min(0).max(1_000_000).nullish(),
  area_rate_cents: z.number().int().min(1).max(100_000_000).nullish(),
  bedrooms: z.number().min(0).max(100).nullish(),
  bathrooms: z.number().min(0).max(100).nullish(),
  floors: z.number().int().min(0).max(50).nullish(),
  occupancy_status: z.string().max(40).nullish(),
  pets_description: z.string().max(500).nullish(),
  access_notes: z.string().max(2000).nullish(),
  parking_notes: z.string().max(2000).nullish(),
  internal_notes: z.string().max(4000).nullish(),
  // Geocoding (0083). Dropped, a restore would clear every property's
  // coordinates and leave the time clock unable to say on-site or off until
  // each address was re-picked from autocomplete by hand.
  latitude: z.number().min(-90).max(90).nullish(),
  longitude: z.number().min(-180).max(180).nullish(),
});

const importedGroupTemplateSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(160),
  description: z.string().max(2000).nullish(),
  name_i18n: z.string().max(4000).nullish(),
  description_i18n: z.string().max(20_000).nullish(),
  text_language: z.string().max(16).nullish(),
  notes: z.string().max(8000).nullish(),
  category_code: z.string().max(40).nullish(),
  tags_json: z.string().max(4000).nullish(),
  // The same nested layout the create endpoint requires. A string of `[{}]`
  // is syntactically JSON and would otherwise be stored, then throw in the
  // quote editor when the snapshot's tasks are read.
  rooms_json: z
    .string()
    .min(2)
    .max(500_000)
    .refine((value) => {
      try {
        return z.array(groupTemplateRoomSchema).min(1).max(100).safeParse(JSON.parse(value)).success;
      } catch {
        return false;
      }
    }, 'Saved group rooms are not a valid layout'),
  seed_key: z.string().max(64).nullish(),
  is_favorite: z.number().int().min(0).max(1).nullish(),
  is_active: z.number().int().min(0).max(1).nullish(),
  display_order: z.number().int().min(0).max(10_000).nullish(),
});

export const importPayloadSchema = z.object({
  company: z.looseObject({}).optional(),
  serviceTemplates: z.array(importedServiceTemplateSchema).max(2_000).optional(),
  roomTemplates: z.array(importedRoomTemplateSchema).max(500).optional(),
  groupTemplates: z.array(importedGroupTemplateSchema).max(500).optional(),
  customers: z.array(importedCustomerSchema).max(10_000).optional(),
  properties: z.array(importedPropertySchema).max(20_000).optional(),
});

export type ImportPayload = z.infer<typeof importPayloadSchema>;
export type UpdateCompanyInput = z.infer<typeof updateCompanySchema>;
export type CreateServiceTemplateInput = z.infer<typeof createServiceTemplateSchema>;
export type CreateRoomTemplateInput = z.infer<typeof createRoomTemplateSchema>;
